import React, { useCallback, useEffect } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { ScouterTestPage } from ".";
import { FirebaseApplicant } from "../../../core/entities/applicant";
import {
  updateApplicantRequest,
  resetIsUpdateSuccessful,
  fetchApplicantsListRequest,
  fetchTotalApplicantsRequest,
  updateTotalNewApplicants,
} from "../../../redux/applicant/actions";
import {
  FETCH_APPLICANTS_REQUEST,
  UPDATE_APPLICANT_REQUEST,
  SEARCH_APPLICANT_REQUEST,
  FETCH_APPLICANTS_LIST_REQUEST,
} from "../../../redux/applicant/actionTypes";
import { clearSpecificRequest } from "../../../redux/request/action";
import { Alert } from "../../atoms/Alert";

const ScouterTestPageContainer = () => {
  const dispatch = useDispatch();

  const dispatchUpdateApplicant = useCallback(
    (id: string, params: Partial<FirebaseApplicant>) =>
      dispatch(updateApplicantRequest(id, params)),
    [dispatch]
  );

  const updateRequest = useSelector(
    (state: RootStateOrAny) => state.requestStore?.[UPDATE_APPLICANT_REQUEST]
  );

  const applicantsList = useSelector(
    (state: RootStateOrAny) =>
      state.requestStore?.[FETCH_APPLICANTS_LIST_REQUEST]
  );

  const totalApplicants = useSelector(
    (state: RootStateOrAny) => state.applicant.totalApplicants
  );

  const dispatchFetchPsychologicalExam = useCallback(
    (
      lastElementDocs?: unknown,
      limit = 10,
      subsStatus = undefined,
      position = undefined
    ) =>
      dispatch(
        fetchApplicantsListRequest({
          where: [
            ["applicationStatus.currentStatus", "==", "Psychological Exam"],
            ["applicationStatus.subStatus", "in", subsStatus],
            ["positionApplied", "in", position],
            ["isDeleted", "==", false],
          ],
          orderBy: [["applicationStatus.dateMoved", "desc"]],
          limit,
          startAfter: lastElementDocs,
        })
      ),
    [dispatch]
  );

  const dispatchUpdateTotalNewApplicants = useCallback(
    (currentPhase: string, prevPhase: string, currentStatus: string) =>
      dispatch(
        updateTotalNewApplicants(currentPhase, prevPhase, currentStatus)
      ),
    [dispatch]
  );

  const dispatchFetchTotalApplicants = useCallback(
    () => dispatch(fetchTotalApplicantsRequest()),
    [dispatch]
  );

  const isUpdating = useSelector(
    (state: RootStateOrAny) => state.applicant.isUpdating
  );

  useEffect(() => {
    dispatch(dispatchFetchPsychologicalExam());
    dispatch(dispatchFetchTotalApplicants());
    dispatch(clearSpecificRequest(SEARCH_APPLICANT_REQUEST));
    return () => {
      dispatch(clearSpecificRequest(FETCH_APPLICANTS_REQUEST));
      dispatch(clearSpecificRequest(UPDATE_APPLICANT_REQUEST));
      dispatch(clearSpecificRequest(FETCH_APPLICANTS_LIST_REQUEST));
      dispatch(resetIsUpdateSuccessful());
    };
  }, [dispatch]);

  const total = totalApplicants?.psychologicalExam?.current || 0;
  const currentStatus =
    updateRequest?.response?.["applicationStatus.currentStatus"];
  const subStatus = updateRequest?.response?.["applicationStatus.subStatus"];

  return (
    <>
      <ScouterTestPage
        tableData={applicantsList}
        isLoading={applicantsList?.isLoading}
        onUpdateApplicant={dispatchUpdateApplicant}
        error={updateRequest?.error}
        isUpdating={isUpdating}
        total={total}
        updateTotalNewApplicants={dispatchUpdateTotalNewApplicants}
        fetchApplicants={dispatchFetchPsychologicalExam}
        fetchTotalApplicants={dispatchFetchTotalApplicants}
      />
      {updateRequest?.didSucceed &&
        (currentStatus === "Failed" || subStatus === "Declined") && (
          <Alert
            type="success"
            message={
              currentStatus === "Failed"
                ? "Applicant moved to failed status"
                : "Successfully moved applicant’s status"
            }
            showIcon
          />
        )}
      {updateRequest?.error && (
        <Alert type="error" message={updateRequest?.error} showIcon />
      )}
    </>
  );
};

export default ScouterTestPageContainer;
